import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import {
  CheckIcon,
  UserGroupIcon,
  ChartBarIcon,
  CalendarIcon,
  StarIcon,
} from "@heroicons/react/20/solid";
import { ForwardRefExoticComponent, SVGProps } from "react";
import { useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import asianStudentImage from "../../assets/asian-martial-arts-student.jpg";
import femaleStudentImage from "../../assets/female-martial-arts-student.jpg";
import testimonialImage from "../../assets/jason_greenfield.png";
import dashboardScreenshot from "../../assets/dashboard-screenshot.jpg";
import martialArtsStudioOwner from "../../assets/martial-arts-studio-owner.png";
import backgroundHeroHomePageCta from "../../assets/background-hero-home-page-cta.jpg";

interface Feature {
  name: string;
  description: string;
  icon: ForwardRefExoticComponent<SVGProps<SVGSVGElement>>;
}

const testimonials = [
  {
    content:
      "DojoTrack has made running our martial arts studio so much easier. Tracking students and keeping up with belt progress is super simple now, and it saves us a ton of time every week. The AI features are pretty insightful, too!",
    author: "Jason Greenfield",
    role: "Owner, Theta Combat",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
  {
    content:
      "DojoTrack has revolutionized how we manage our martial arts studio. This AI software for martial arts makes student tracking and belt progression intuitive and saves us hours every week.",
    author: "Sarah Chen",
    role: "Owner, Dragon's Path Martial Arts",
    image:
      "https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
  {
    content:
      "As a multi-location dojo owner, I needed software martial arts instructors could trust. DojoTrack's martial arts CRM has been exactly what we needed for our growing student base.",
    author: "Michael Rodriguez",
    role: "Head Instructor, United Martial Arts",
    image:
      "https://images.unsplash.com/photo-1519244703995-f4e0f30006d5?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80",
  },
];

const features: Feature[] = [
  {
    name: "Student Management",
    description:
      "Complete student lifecycle management with AI-powered insights to reduce drop-offs and increase retention. DojoTrack's martial arts CRM includes Stripe integration for payments, membership management, and progress tracking.",
    icon: UserGroupIcon,
  },
  {
    name: "Analytics Dashboard",
    description:
      "Real-time insights into your studio's performance with actionable metrics for students, leads, attendance, and financials. This AI software for martial arts identifies growth opportunities and enables data-driven decisions.",
    icon: ChartBarIcon,
  },
  {
    name: "Lead Management",
    description:
      "Convert more prospects with AI-powered lead scoring, website form capture, and automated SMS follow-ups. Connect your own Twilio account or rent a number from DojoTrack. Leads automatically receive appointment scheduling links via text. Track, nurture, and convert leads efficiently to grow your dojo.",
    icon: CalendarIcon,
  },
];

const Home = () => {
  const [activeSection, setActiveSection] = useState("dashboard");
  const [openFAQ, setOpenFAQ] = useState<number | null>(null);

  const toggleFAQ = (index: number) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const [tourStep, setTourStep] = useState(0);
  const tourSteps = [
    {
      title: "Student Management",
      description: "Easily add new students and track their progress through belt levels",
      demo: "student-registration",
      benefit: "Powerful dashboard that predicts student retention and manages everything from memberships to progress tracking"
    },
    {
      title: "Payment Processing", 
      description: "Automated billing and payment tracking with Stripe integration",
      demo: "payment-processing",
      benefit: "Students can easily update payment methods through Stripe's secure portal - never miss a payment again"
    },
    {
      title: "Digital Waivers",
      description: "Create custom waivers and let leads sign them digitally",
      demo: "waivers",
      benefit: "Create waivers in minutes, share via link, and instantly convert signees to leads or students"
    },
    {
      title: "Email & SMS Messaging",
      description: "Send personalized messages to students and leads with AI assistance",
      demo: "messaging",
      benefit: "Improve communication and retention"
    },
    {
      title: "Attendance Tracking",
      description: "Track student attendance with an intuitive kiosk interface",
      demo: "attendance",
      benefit: "Monitor engagement and progress"
    },
    {
      title: "Analytics & Insights",
      description: "AI-powered insights help you grow your studio and retain students",
      demo: "analytics",
      benefit: "Make data-driven decisions"
    }
  ];

  const currentTourStep = tourSteps[tourStep];

  const renderTourDemo = (step: string) => {
    switch (step) {
      case "student-registration":
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Students</h3>
                <button className="bg-indigo-600 text-white px-3 py-1.5 rounded-md text-sm font-medium hover:bg-indigo-700">
                  Add Student
                </button>
              </div>
            </div>

            {/* Filters */}
            <div className="px-6 py-4 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
              <div className="flex flex-wrap gap-4">
                <input
                  type="text"
                  placeholder="Search students..."
                  className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
                <select className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white">
                  <option>All Status</option>
                  <option>Active</option>
                  <option>Inactive</option>
                </select>
              </div>
            </div>

            {/* Student Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead className="bg-gray-50 dark:bg-gray-700">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Belt</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">Risk</th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full overflow-hidden">
                          <img 
                            src={asianStudentImage} 
                            alt="Alex Chen" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">Alex Chen</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">alex@email.com</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-white text-gray-800 border border-gray-300">
                        White Belt
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 mr-2">
                          <div
                            className="h-2.5 rounded-full bg-green-500"
                            style={{ width: "15%" }}
                          />
                        </div>
                        <span className="text-xs text-gray-900 dark:text-white">15%</span>
                      </div>
                    </td>
                  </tr>
                  <tr className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-8 h-8 rounded-full overflow-hidden">
                          <img 
                            src={femaleStudentImage} 
                            alt="Maria Rodriguez" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">Maria Rodriguez</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400">maria@email.com</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        Green Belt
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                        Active
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="w-16 bg-gray-200 dark:bg-gray-600 rounded-full h-2.5 mr-2">
                          <div
                            className="h-2.5 rounded-full bg-yellow-500"
                            style={{ width: "45%" }}
                          />
                        </div>
                        <span className="text-xs text-gray-900 dark:text-white">45%</span>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        );
      
      case "payment-processing":
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Recent Payments</h3>
            </div>

            {/* Payment Cards */}
            <div className="p-6 space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Sarah Johnson</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Monthly Membership</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-green-600 dark:text-green-400">$89.00</p>
                    <p className="text-sm text-green-600 dark:text-green-400">✓ Processed</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M4 4a2 2 0 00-2 2v1h16V6a2 2 0 00-2-2H4zM18 9H2v5a2 2 0 002 2h12a2 2 0 002-2V9zM4 13a1 1 0 011-1h1a1 1 0 110 2H5a1 1 0 01-1-1zm5-1a1 1 0 100 2h1a1 1 0 100-2H9z" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium text-gray-900 dark:text-white">Alex Chen</p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">Uniform Purchase</p>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-lg font-semibold text-blue-600 dark:text-blue-400">$45.00</p>
                    <p className="text-sm text-blue-600 dark:text-blue-400">✓ Processed</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case "waivers":
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Waivers & Legal Documents</h3>
            </div>

            {/* Waiver Content */}
            <div className="p-6 space-y-4">
              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2a8 8 0 00-8 8c0 2.15.85 4 2 5.25V18a2 2 0 002 2h12a2 2 0 002-2v-2.75C17.15 16 18 14.15 18 12a8 8 0 00-8-8zm0 14a6 6 0 100-12 6 6 0 000 12zm-3-5a1 1 0 11-2 0 1 1 0 012 0zm6 0a1 1 0 11-2 0 1 1 0 012 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Waiver Management</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Create, customize, and manage digital waivers for students.</p>
                  </div>
                </div>
              </div>

              <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-800 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M13 10V3L4 14h7v7l9-11h-7V3z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Digital Signature</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Let students sign waivers securely and digitally.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case "messaging":
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Email & SMS Messaging</h3>
            </div>

            {/* Messaging Content */}
            <div className="p-6 space-y-4">
              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M2 3a1 1 0 011-1h2.153a3 3 0 012.45.69l1.804 1.804A3 3 0 0010.153 5H17a1 1 0 011 1v3.153a3 3 0 01-.69 2.45l-1.804 1.804A3 3 0 0014.153 15H11a1 1 0 01-1-1v-2a1 1 0 00-1-1H6a1 1 0 01-1-1V5a1 1 0 011-1h2a1 1 0 001-1V3a1 1 0 01-1-1H2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">AI-Powered Messaging</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Send personalized messages to students and leads with AI assistance.</p>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Smart Notifications</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Receive relevant notifications and updates directly in your inbox.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case "attendance":
        return (
          <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Attendance Tracking</h3>
            </div>

            {/* Attendance Content */}
            <div className="p-6 space-y-4">
              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2a8 8 0 00-8 8c0 2.15.85 4 2 5.25V18a2 2 0 002 2h12a2 2 0 002-2v-2.75C17.15 16 18 14.15 18 12a8 8 0 00-8-8zm0 14a6 6 0 100-12 6 6 0 000 12zm-3-5a1 1 0 11-2 0 1 1 0 012 0zm6 0a1 1 0 11-2 0 1 1 0 012 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Kiosk Mode</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Streamline attendance with a user-friendly kiosk interface.</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 2a8 8 0 00-8 8c0 2.15.85 4 2 5.25V18a2 2 0 002 2h12a2 2 0 002-2v-2.75C17.15 16 18 14.15 18 12a8 8 0 00-8-8zm0 14a6 6 0 100-12 6 6 0 000 12zm-3-5a1 1 0 11-2 0 1 1 0 012 0zm6 0a1 1 0 11-2 0 1 1 0 012 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">Real-Time Updates</p>
                    <p className="text-sm text-gray-500 dark:text-gray-400">Attendance data is updated instantly across all devices.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case "analytics":
        return (
          <div className="space-y-6">
            {/* Dashboard Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Active Students</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">127</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Monthly Revenue</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">$12,450</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
                <div className="flex items-center">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M3 4a1 1 0 011-1h12a1 1 0 011 1v2a1 1 0 01-1 1H4a1 1 0 01-1-1V4zM3 10a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H4a1 1 0 01-1-1v-6zM14 9a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-4">
                    <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Retention Rate</p>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">94%</p>
                  </div>
                </div>
              </div>
            </div>

            {/* AI Insights */}
            <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">AI Insights</h3>
              </div>
              <div className="p-6 space-y-4">
                <div className="flex items-center p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">📈 Student Growth</p>
                    <p className="text-sm text-green-600 dark:text-green-400">+12 students this month</p>
                  </div>
                </div>
                <div className="flex items-center p-4 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <div className="flex-shrink-0">
                    <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center">
                      <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" />
                      </svg>
                    </div>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900 dark:text-white">🎯 Retention Alert</p>
                    <p className="text-sm text-yellow-600 dark:text-yellow-400">3 students at risk of leaving</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  const renderDashboardContent = () => (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Dashboard</h2>
        <p className="text-gray-400">Welcome back, Sensei Sarah</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Active Students</p>
              <p className="text-2xl font-bold text-white">127</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Classes Today</p>
              <p className="text-2xl font-bold text-white">8</p>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              </div>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-400">Monthly Revenue</p>
              <p className="text-2xl font-bold text-white">$12,450</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4">Recent Activity</h3>
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
            <span className="text-gray-300">New student registration: Alex Chen</span>
            <span className="text-gray-500 text-sm">2 min ago</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span className="text-gray-300">Payment received: Sarah Johnson</span>
            <span className="text-gray-500 text-sm">15 min ago</span>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
            <span className="text-gray-300">Belt promotion: Mike Rodriguez</span>
            <span className="text-gray-500 text-sm">1 hour ago</span>
          </div>
        </div>
      </div>
    </>
  );

  const renderStudentsContent = () => (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Students</h2>
        <p className="text-gray-400">Manage your student roster and track progress</p>
      </div>

      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-white">Recent Students</h3>
          <button className="bg-indigo-600 text-white px-3 py-1 rounded text-sm">Add Student</button>
        </div>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-700 rounded">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-600">
                <img 
                  src={asianStudentImage} 
                  alt="Alex Chen" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-white font-medium">Alex Chen</p>
                <p className="text-gray-400 text-sm">White Belt • Joined 2 days ago</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-green-400 text-sm font-medium">Active</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-700 rounded">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 rounded-full overflow-hidden border border-gray-600">
                <img 
                  src={femaleStudentImage} 
                  alt="Maria Rodriguez" 
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-white font-medium">Maria Rodriguez</p>
                <p className="text-gray-400 text-sm">Green Belt • Member for 6 months</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-green-400 text-sm font-medium">Active</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-700 rounded">
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-semibold">SJ</span>
              </div>
              <div>
                <p className="text-white font-medium">Sarah Johnson</p>
                <p className="text-gray-400 text-sm">Blue Belt • Member for 1 year</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-green-400 text-sm font-medium">Active</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderClassesContent = () => (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Classes</h2>
        <p className="text-gray-400">Today's class schedule and attendance</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Today's Schedule</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-700 rounded">
              <div>
                <p className="text-white font-medium">Kids Karate (Ages 6-9)</p>
                <p className="text-gray-400 text-sm">4:00 PM - 5:00 PM</p>
              </div>
              <div className="text-right">
                <p className="text-green-400 text-sm">12/15 enrolled</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-700 rounded">
              <div>
                <p className="text-white font-medium">Adult Karate</p>
                <p className="text-gray-400 text-sm">6:00 PM - 7:30 PM</p>
              </div>
              <div className="text-right">
                <p className="text-green-400 text-sm">18/20 enrolled</p>
              </div>
            </div>
            <div className="flex items-center justify-between p-3 bg-gray-700 rounded">
              <div>
                <p className="text-white font-medium">Advanced Training</p>
                <p className="text-gray-400 text-sm">7:30 PM - 8:30 PM</p>
              </div>
              <div className="text-right">
                <p className="text-green-400 text-sm">8/10 enrolled</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
          <div className="space-y-3">
            <button className="w-full bg-indigo-600 text-white p-3 rounded text-left">
              <p className="font-medium">Create New Class</p>
              <p className="text-indigo-200 text-sm">Add a new class to your schedule</p>
            </button>
            <button className="w-full bg-green-600 text-white p-3 rounded text-left">
              <p className="font-medium">Take Attendance</p>
              <p className="text-green-200 text-sm">Mark attendance for today's classes</p>
            </button>
            <button className="w-full bg-purple-600 text-white p-3 rounded text-left">
              <p className="font-medium">View Calendar</p>
              <p className="text-purple-200 text-sm">See full month schedule</p>
            </button>
          </div>
        </div>
      </div>
    </>
  );

  const renderAnalyticsContent = () => (
    <>
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-white">Analytics</h2>
        <p className="text-gray-400">Track your studio's performance and growth</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Student Growth</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">This Month</span>
              <span className="text-green-400 font-semibold">+12 students</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Last Month</span>
              <span className="text-blue-400 font-semibold">+8 students</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Retention Rate</span>
              <span className="text-green-400 font-semibold">94%</span>
            </div>
          </div>
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Revenue Overview</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Monthly Revenue</span>
              <span className="text-white font-semibold">$12,450</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Avg. per Student</span>
              <span className="text-white font-semibold">$98</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Growth Rate</span>
              <span className="text-green-400 font-semibold">+15%</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-800 rounded-lg p-6 mt-6">
        <h3 className="text-lg font-semibold text-white mb-4">Top Performing Classes</h3>
        <div className="space-y-3">
          <div className="flex items-center justify-between p-3 bg-gray-700 rounded">
            <div>
              <p className="text-white font-medium">Kids Karate</p>
              <p className="text-gray-400 text-sm">Ages 6-9</p>
            </div>
            <div className="text-right">
              <p className="text-white font-semibold">32 students</p>
              <p className="text-green-400 text-sm">+8 this month</p>
            </div>
          </div>
          <div className="flex items-center justify-between p-3 bg-gray-700 rounded">
            <div>
              <p className="text-white font-medium">Adult Karate</p>
              <p className="text-gray-400 text-sm">All levels</p>
            </div>
            <div className="text-right">
              <p className="text-white font-semibold">28 students</p>
              <p className="text-green-400 text-sm">+5 this month</p>
            </div>
          </div>
        </div>
      </div>
    </>
  );

  const renderContent = () => {
    switch (activeSection) {
      case "dashboard":
        return renderDashboardContent();
      case "students":
        return renderStudentsContent();
      case "classes":
        return renderClassesContent();
      case "analytics":
        return renderAnalyticsContent();
      default:
        return renderDashboardContent();
    }
  };

  return (
    <>
      <Helmet>
        <title>
          AI Powered Martial Arts CRM & Management Software | DojoTrack
        </title>
        <meta
          name="description"
          content="DojoTrack - AI software for martial arts studios. Complete martial arts CRM, student management, and software martial arts schools trust. Start your 14-day free trial today."
        />
        <meta
          name="keywords"
          content="dojotrack, software martial arts, AI software martial arts, martial arts crm, martial arts management software, dojo software, student management, attendance tracking, martial arts studio software"
        />
        <meta name="author" content="DojoTrack LLC" />
        <meta
          property="og:title"
          content="AI Powered Martial Arts CRM & Management Software | DojoTrack"
        />
        <meta
          property="og:description"
          content="Streamline your martial arts studio with DojoTrack's AI-powered management system. Track students, manage classes, handle payments, and grow your dojo with our comprehensive platform."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://dojotrack.com" />
        <meta property="og:image" content="https://dojotrack.com/social-media-share-dojotrack.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="AI Powered Martial Arts CRM & Management Software | DojoTrack"
        />
        <meta
          name="twitter:description"
          content="Streamline your martial arts studio with DojoTrack's AI-powered management system. Track students, manage classes, handle payments, and grow your dojo with our comprehensive platform."
        />
        <meta name="twitter:image" content="https://dojotrack.com/social-media-share-dojotrack.png" />
        <link rel="canonical" href="https://dojotrack.com" />
      </Helmet>
      <div className="bg-white dark:bg-gray-900">
        {/* Hero section */}
        <div className="relative isolate overflow-hidden bg-gradient-to-b from-indigo-100/20 dark:from-indigo-900/20">
          {/* Textured background overlay */}
          <div className="absolute inset-0 opacity-20 dark:opacity-10 z-0" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E\")"}}></div>
          {/* Geometric pattern overlay */}
          <div className="absolute inset-0 opacity-30 dark:opacity-20 z-0" style={{backgroundImage: "url(\"data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cdefs%3E%3Cpattern id='grid' width='20' height='20' patternUnits='userSpaceOnUse'%3E%3Cpath d='M 20 0 L 0 0 0 20' fill='none' stroke='%23ffffff' stroke-width='0.5' opacity='0.1'/%3E%3C/pattern%3E%3C/pattern%3E%3C/defs%3E%3Crect width='100' height='100' fill='url(%23grid)'/%3E%3C/svg%3E\")"}}></div>
          {/* Radial gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-indigo-500/10 via-transparent to-purple-500/10 z-0"></div>
          <div className="mx-auto max-w-7xl pb-16 pt-8 sm:pb-20 lg:grid lg:grid-cols-2 lg:gap-x-8 lg:px-8 lg:py-20">
            <div className="px-6 lg:px-0 lg:pt-4 relative z-10 flex items-center">
              <div className="mx-auto max-w-2xl">
                <div className="max-w-lg">
                  <h1 className="mt-10 text-4xl font-bold tracking-tight sm:text-6xl">
                    <span className="text-gray-900 dark:text-white">DojoTrack: </span>
                    <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">AI Software</span>
                    <br />
                    <span className="text-gray-900 dark:text-white">for Martial Arts Management & CRM</span>
                  </h1>
                  <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-400">
                    DojoTrack is the leading AI software for <strong className="font-bold">US-based</strong> martial arts
                    studios, offering a complete martial arts CRM to manage
                    students, track progress, and grow your business with
                    intelligent automation.
                  </p>
                  <div className="mt-10 flex items-center gap-x-6">
                    <Link
                      to="/signup"
                      className="rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-lg hover:from-indigo-500 hover:to-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all transform hover:scale-105 hover:shadow-xl"
                    >
                      14 Day Free Trial
                    </Link>
                    <Link
                      to="/features"
                      className="text-sm font-semibold leading-6 text-gray-900 dark:text-white"
                    >
                      View All Features <span aria-hidden="true">→</span>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
            {/* Interactive Software Demo - Commented Out */}
            {/* 
            <div className="mt-20 sm:mt-24 md:mx-auto md:max-w-2xl lg:mx-0 lg:mt-0 lg:w-full relative z-0">
              <div className="shadow-lg md:rounded-3xl overflow-hidden">
                <div className="bg-white dark:bg-gray-900">
                  <div className="relative">
                    <div className="mx-auto max-w-2xl md:mx-0 md:max-w-none">
                      <div className="w-full bg-white dark:bg-gray-900">
                        <div className="flex items-center justify-between bg-gray-100 dark:bg-gray-800 px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-700">
                          <div className="flex items-center space-x-3">
                            <div className="w-8 h-8 bg-indigo-600 rounded-lg flex items-center justify-center">
                              <span className="text-white text-sm font-bold">DT</span>
                            </div>
                            <span className="text-gray-900 dark:text-white font-semibold">DojoTrack</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <span className="text-gray-500 dark:text-gray-400 text-sm">Live Demo</span>
                          </div>
                        </div>
                        <div className="p-4 sm:p-6">
                          <div className="mb-4 sm:mb-6">
                            <div className="flex items-center justify-between mb-2">
                              <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">{currentTourStep.title}</h3>
                              <div className="flex space-x-1">
                                {tourSteps.map((_, index) => (
                                  <button
                                    key={index}
                                    onClick={() => setTourStep(index)}
                                    className={`w-2 h-2 rounded-full transition-colors ${
                                      index === tourStep ? 'bg-indigo-500' : 'bg-gray-300 dark:bg-gray-600'
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                            <p className="text-gray-600 dark:text-gray-400 mb-3 sm:mb-4 text-sm sm:text-base">{currentTourStep.description}</p>
                            <div className="bg-indigo-50 dark:bg-indigo-900/20 border border-indigo-200 dark:border-indigo-500/20 rounded-lg p-3">
                              <p className="text-indigo-700 dark:text-indigo-300 text-sm font-medium">💡 {currentTourStep.benefit}</p>
                            </div>
                          </div>
                          <div className="mb-4 sm:mb-6">
                            {renderTourDemo(currentTourStep.demo)}
                          </div>
                          <div className="flex items-center justify-between">
                            <button
                              onClick={() => setTourStep(Math.max(0, tourStep - 1))}
                              disabled={tourStep === 0}
                              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              ← Previous
                            </button>
                            <span className="text-gray-500 dark:text-gray-400 text-sm">
                              {tourStep + 1} of {tourSteps.length}
                            </span>
                            <button
                              onClick={() => setTourStep(Math.min(tourSteps.length - 1, tourStep + 1))}
                              disabled={tourStep === tourSteps.length - 1}
                              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-white disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                            >
                              Next →
                            </button>
                          </div>
                          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200 dark:border-gray-700">
                            <div className="text-center">
                              <p className="text-gray-600 dark:text-gray-300 mb-3 text-sm sm:text-base">Ready to see DojoTrack in action?</p>
                              <Link
                                to="/signup"
                                className="inline-flex items-center bg-indigo-600 text-white px-3 sm:px-4 py-2 rounded-md hover:bg-indigo-500 transition-colors text-sm sm:text-base"
                              >
                                Start 14-Day Free Trial
                                <svg className="ml-2 w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                              </Link>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            */}

            {/* YouTube Video Replacement */}
            <div className="mt-20 sm:mt-24 md:mx-auto md:max-w-2xl lg:mx-0 lg:mt-0 lg:w-full relative z-0 flex items-center px-6 lg:px-0">
              <div className="shadow-lg rounded-3xl overflow-hidden w-full">
                <div className="bg-white dark:bg-gray-900">
                  <div className="relative">
                    <div className="aspect-video w-full relative">
                      <iframe
                        className="w-full h-full rounded-lg"
                        src="https://www.youtube.com/embed/ZTaOEQWdnU8?modestbranding=1&rel=0&showinfo=0&controls=1&disablekb=0&fs=1&iv_load_policy=3&cc_load_policy=0"
                        title="DojoTrack Demo Video"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        allowFullScreen
                      ></iframe>
                      <style>{`
                        .ytp-title-channel,
                        .ytp-chrome-top,
                        .ytp-show-cards-title,
                        .ytp-watermark {
                          display: none !important;
                        }
                      `}</style>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="absolute inset-x-0 bottom-0 -z-10 h-24 bg-gradient-to-t from-white dark:from-gray-900 sm:h-32" />
        </div>

        {/* AI Benefits section with Martial Arts Studio Owner Image */}
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-5 lg:gap-12 items-center">
            {/* Left: Dashboard Screenshot */}
            <div className="lg:col-span-3">
              <img
                src={dashboardScreenshot}
                alt="DojoTrack Dashboard Screenshot"
                className="w-full h-auto rounded-lg shadow-sm"
              />
            </div>

            {/* Right: Text Content */}
            <div className="lg:col-span-2">
              <h3 className="text-base font-bold leading-7 text-indigo-600 dark:text-indigo-400">
                Why Choose DojoTrack's AI Software for Martial Arts?
              </h3>
              <p className="mt-4 text-lg leading-8 text-gray-600 dark:text-gray-400">
                DojoTrack isn't just software martial arts schools use—it's an
                intelligent martial arts CRM that learns from your data to provide
                actionable insights, and predict student retention risks, and
                automate routine tasks so you can focus on teaching.
              </p>
            </div>
          </div>
        </div>

        {/* Feature section */}
        <div className="bg-gray-50 dark:bg-gray-800/50 py-24 sm:py-32">
          <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-400">
              Faster growth
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight sm:text-4xl">
              <span className="text-gray-900 dark:text-white">Complete martial arts management software with </span>
              <span className="bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">AI-powered insights</span>
            </p>
            <p className="mt-6 text-lg leading-8 text-gray-600 dark:text-gray-400">
              DojoTrack's martial arts CRM combines software martial arts
              instructors trust with AI-powered analytics, lead conversion, and
              complete martial arts management software features to grow your
              business efficiently.
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              {features.map((feature) => (
                <div key={feature.name} className="flex flex-col h-full bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6 shadow-sm hover:shadow-xl hover:border-indigo-300 dark:hover:border-indigo-600 transition-all transform hover:-translate-y-1">
                  <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900 dark:text-white mb-4">
                    <feature.icon
                      className="h-5 w-5 flex-none text-indigo-600 dark:text-indigo-400"
                      aria-hidden="true"
                    />
                    {feature.name}
                  </dt>
                  <dd className="flex flex-col text-base leading-7 text-gray-600 dark:text-gray-400 flex-1">
                    <p className="flex-1">{feature.description}</p>
                  </dd>
                </div>
              ))}
            </dl>
            <div className="mt-12 flex justify-center">
              <Link
                to="/features"
                className="rounded-md bg-gradient-to-r from-indigo-600 to-purple-600 px-5 py-2.5 text-sm font-semibold text-white shadow-lg hover:from-indigo-500 hover:to-purple-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 transition-all transform hover:scale-105 hover:shadow-xl"
              >
                View all features
              </Link>
            </div>
          </div>
          </div>
        </div>

        {/* Testimonials section */}
        <div className="mx-auto max-w-7xl px-6 py-24 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600 dark:text-indigo-400">
              Testimonials
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl">
              Loved by martial arts studio owners
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl">
            <div className="flex flex-col justify-between bg-white dark:bg-gray-800 p-8 ring-1 ring-gray-200 dark:ring-gray-700 rounded-2xl shadow-sm">
              <div>
                <div className="flex items-center justify-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <StarIcon key={i} className="h-5 w-5 text-yellow-400" />
                  ))}
                </div>
                <blockquote className="text-gray-900 dark:text-white text-center">
                  <p className="text-lg leading-7">"DojoTrack has made running our martial arts studio so much easier. Tracking students and keeping up with belt progress is super simple now, and it saves us a ton of time every week. The AI features are pretty insightful, too!"</p>
                </blockquote>
              </div>
              <div className="mt-6 flex items-center justify-center gap-x-4">
                <img
                  className="h-10 w-10 rounded-full bg-gray-50 dark:bg-gray-700"
                  src={testimonialImage}
                  alt=""
                />
                <div className="text-center">
                  <div className="font-semibold text-gray-900 dark:text-white">
                    Jason Greenfield
                  </div>
                  <div className="text-gray-600 dark:text-gray-400">Owner, Theta Combat</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FAQ section */}
        <div className="bg-gray-50 dark:bg-gray-800/50">
          <div className="mx-auto max-w-7xl px-6 pt-24 pb-0 sm:py-32 lg:px-8">
          <div className="mx-auto max-w-4xl divide-y divide-gray-900/10 dark:divide-gray-700/10">
            <h2 className="text-2xl font-bold leading-10 tracking-tight text-gray-900 dark:text-white">
              Frequently asked questions
            </h2>
            <dl className="mt-10 space-y-4">
              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow">
                <dt>
                  <button
                    onClick={() => toggleFAQ(0)}
                    className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
                  >
                    <h3 className="text-lg font-semibold leading-7 text-gray-900 dark:text-white pr-4">
                      How does the 14-day free trial work?
                    </h3>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900 flex-shrink-0">
                      {openFAQ === 0 ? (
                        <ChevronUpIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      )}
                    </span>
                  </button>
                </dt>
                {openFAQ === 0 && (
                  <dd className="px-6 pb-6 text-base leading-7 text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-4">
                    Our 14-day free trial gives you full access to all features of
                    DojoTrack's martial arts CRM.{" "}
                    <strong className="text-gray-900 dark:text-white">
                      A credit card is required
                    </strong>
                    . You select your subscription plan during the{" "}
                    <Link
                      to="/pricing"
                      className="text-indigo-600 dark:text-indigo-400 hover:underline font-medium"
                    >
                      onboarding process
                    </Link>
                    . You can import your student data, set up your classes, and
                    experience how DojoTrack can help manage your studio. At the
                    end of the trial, you are automatically billed for your
                    selected plan and charged immediately, then billed each month
                    thereafter.
                  </dd>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow">
                <dt>
                  <button
                    onClick={() => toggleFAQ(1)}
                    className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
                  >
                    <h3 className="text-lg font-semibold leading-7 text-gray-900 dark:text-white pr-4">
                      Can I upgrade or downgrade my plan later?
                    </h3>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900 flex-shrink-0">
                      {openFAQ === 1 ? (
                        <ChevronUpIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      )}
                    </span>
                  </button>
                </dt>
                {openFAQ === 1 && (
                  <dd className="px-6 pb-6 text-base leading-7 text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-4">
                    Yes, you can change your DojoTrack plan at any time. If you
                    need to accommodate more students, you can upgrade to a higher
                    tier. If you need to reduce costs, you can downgrade to a
                    lower tier, as long as your current student count fits within
                    the new tier's limits.
                  </dd>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow">
                <dt>
                  <button
                    onClick={() => toggleFAQ(2)}
                    className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
                  >
                    <h3 className="text-lg font-semibold leading-7 text-gray-900 dark:text-white pr-4">
                      Can DojoTrack's martial arts management software be
                      customized for different martial arts styles?
                    </h3>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900 flex-shrink-0">
                      {openFAQ === 2 ? (
                        <ChevronUpIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      )}
                    </span>
                  </button>
                </dt>
                {openFAQ === 2 && (
                  <dd className="px-6 pb-6 text-base leading-7 text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-4">
                    Absolutely! DojoTrack's AI software for martial arts is
                    designed to be flexible and can accommodate different martial
                    arts styles and belt systems. You can customize belt ranks,
                    class types, staff roles, events, studio information,
                    attendance data, and more to match your studio's specific
                    needs.
                  </dd>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow">
                <dt>
                  <button
                    onClick={() => toggleFAQ(3)}
                    className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
                  >
                    <h3 className="text-lg font-semibold leading-7 text-gray-900 dark:text-white pr-4">
                      Can the martial arts CRM accept payments for memberships?
                    </h3>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900 flex-shrink-0">
                      {openFAQ === 3 ? (
                        <ChevronUpIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      )}
                    </span>
                  </button>
                </dt>
                {openFAQ === 3 && (
                  <dd className="px-6 pb-6 text-base leading-7 text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-4">
                    Yes! You can connect your Stripe account to DojoTrack and
                    create memberships with prices, charging students monthly. You
                    can also create one-time charges with descriptions, view
                    outstanding charges for students, and see a record of your
                    financial metrics right on your dashboard.
                  </dd>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow">
                <dt>
                  <button
                    onClick={() => toggleFAQ(4)}
                    className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
                  >
                    <h3 className="text-lg font-semibold leading-7 text-gray-900 dark:text-white pr-4">
                      What payment processors does DojoTrack work with?
                    </h3>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900 flex-shrink-0">
                      {openFAQ === 4 ? (
                        <ChevronUpIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      )}
                    </span>
                  </button>
                </dt>
                {openFAQ === 4 && (
                  <dd className="px-6 pb-6 text-base leading-7 text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-4">
                    DojoTrack currently integrates with{" "}
                    <strong className="text-gray-900 dark:text-white">
                      Stripe
                    </strong>
                    , the industry-leading payment processor trusted by millions
                    of businesses worldwide. We chose Stripe because it offers the
                    most comprehensive features for martial arts studios,
                    including seamless subscription management, automatic
                    recurring billing, secure payment processing, and detailed
                    financial reporting. Stripe's robust API allows us to provide
                    you with the best possible experience for creating students,
                    managing memberships, and handling payments. While we're
                    committed to expanding our payment processor options in the
                    future to give you more flexibility, Stripe currently provides
                    everything you need to run your studio's finances efficiently
                    and professionally.
                  </dd>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow">
                <dt>
                  <button
                    onClick={() => toggleFAQ(5)}
                    className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
                  >
                    <h3 className="text-lg font-semibold leading-7 text-gray-900 dark:text-white pr-4">
                      How can I easily add my existing students and leads to
                      DojoTrack?
                    </h3>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900 flex-shrink-0">
                      {openFAQ === 5 ? (
                        <ChevronUpIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      )}
                    </span>
                  </button>
                </dt>
                {openFAQ === 5 && (
                  <dd className="px-6 pb-6 text-base leading-7 text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-4">
                    DojoTrack makes it simple to import your existing data! You
                    can upload a <strong>CSV</strong>,{" "}
                    <strong>Excel (XLSX)</strong>, or <strong>JSON</strong> file
                    containing your students and leads—no manual data entry
                    required! Our intelligent column matching system automatically
                    maps your data to the correct fields, so you can quickly get
                    all your contacts into DojoTrack. We also provide a
                    downloadable template to help you format your data correctly.
                    The column matching feature ensures your data is properly
                    organized and ready to use immediately. Additionally, if you're
                    already using Stripe for payments, you can import your existing
                    Stripe customers as students with their payment methods
                    automatically synced to DojoTrack.
                  </dd>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow">
                <dt>
                  <button
                    onClick={() => toggleFAQ(6)}
                    className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
                  >
                    <h3 className="text-lg font-semibold leading-7 text-gray-900 dark:text-white pr-4">
                      Can I import my existing Stripe customers into DojoTrack?
                    </h3>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900 flex-shrink-0">
                      {openFAQ === 6 ? (
                        <ChevronUpIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      )}
                    </span>
                  </button>
                </dt>
                {openFAQ === 6 && (
                  <dd className="px-6 pb-6 text-base leading-7 text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-4">
                    Yes! If you're already using Stripe for payments, DojoTrack
                    makes it incredibly easy to import your existing customers as
                    students. Simply connect your Stripe account and use our
                    "Import from Stripe" feature to automatically import all your
                    customers with their payment methods, subscription status, and
                    billing information. This means your students can immediately
                    use their existing payment methods for memberships and
                    one-time payments without having to re-enter their card
                    information. The import process preserves all their payment
                    history and ensures a seamless transition to DojoTrack's
                    martial arts management software. You can also import
                    additional students and leads at any time using CSV, Excel
                    (XLSX), or JSON files.
                  </dd>
                )}
              </div>

              <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-shadow">
                <dt>
                  <button
                    onClick={() => toggleFAQ(7)}
                    className="flex w-full items-center justify-between p-6 text-left hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors rounded-lg"
                  >
                    <h3 className="text-lg font-semibold leading-7 text-gray-900 dark:text-white pr-4">
                      What currencies does DojoTrack process payments in?
                    </h3>
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-900 flex-shrink-0">
                      {openFAQ === 7 ? (
                        <ChevronUpIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      ) : (
                        <ChevronDownIcon className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                      )}
                    </span>
                  </button>
                </dt>
                {openFAQ === 7 && (
                  <dd className="px-6 pb-6 text-base leading-7 text-gray-600 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 pt-4">
                    At the moment, DojoTrack processes payments in{" "}
                    <strong className="text-gray-900 dark:text-white">USD</strong>
                    . We have plans in the future to make DojoTrack process
                    multi-currency payments, allowing you to accept payments in
                    various currencies based on your studio's location and needs.
                  </dd>
                )}
              </div>
            </dl>
          </div>
          </div>
        </div>

        {/* CTA section */}
        <div className="bg-white dark:bg-gray-900">
          <div className="mx-auto max-w-7xl py-24 sm:px-6 sm:py-32 lg:px-8">
            <div 
              className="relative isolate overflow-hidden px-6 py-24 text-center shadow-2xl sm:rounded-3xl sm:px-16"
              style={{
                backgroundImage: `url(${backgroundHeroHomePageCta})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat'
              }}
            >
              {/* Overlay for better text readability */}
              <div className="absolute inset-0 bg-gray-900/70 dark:bg-gray-800/70"></div>
              <div className="relative z-10">
              <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight sm:text-4xl">
                <span className="text-white">Ready to </span>
                <span className="bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">transform</span>
                <span className="text-white"> your martial arts studio with DojoTrack?</span>
              </h2>
              <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-300">
                Start your 14-day free trial today. Experience the power of our
                AI software for martial arts and see how DojoTrack can help your
                studio thrive.
              </p>
              <div className="mt-10 flex items-center justify-center gap-x-6">
                <Link
                  to="/signup"
                  className="rounded-md bg-gradient-to-r from-indigo-500 to-purple-500 px-3.5 py-2.5 text-sm font-semibold text-white shadow-lg hover:from-indigo-400 hover:to-purple-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white transition-all transform hover:scale-105 hover:shadow-xl"
                >
                  Start 14 Day Free Trial
                </Link>
                <Link
                  to="/pricing"
                  className="text-sm font-semibold leading-6 text-white"
                >
                  View pricing <span aria-hidden="true">→</span>
                </Link>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Home;
